import React, { useEffect, useMemo, useState, startTransition, useCallback, useRef } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { Case, Switch, When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { GuildScore } from '@/atoms/GuildScore'
import { CircleCheckOutlineIcon } from '@/atoms/Icons/CircleCheckOutlineIcon'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { CaptionMD, EyebrowSM, LabelSM, ParagraphSM, TitleMD, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  FranchiseLandingItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { getWebClient } from '@/services/ApolloClient/ApolloClient'
import { buildSubtitle, canStreamEpisode, FranchisePageData } from '@/services/FranchiseService'
import { isInGuildAccess, isSellingTickets, isStreaming } from '@/services/PhaseManagerService'
import { Project, Season, getMovieAndSpecialsWatchables } from '@/services/ProjectsService'
import {
  getDefaultCta,
  getTrailerCta,
  isARecap,
  isASneakPeek,
  processEpisodesToMediasWithFiltering,
  processWatchablesToMediasWithFiltering,
  Media,
} from '@/utils/EpisodeUtil'
import { getProjectInEarlyAccess } from '@/utils/GuildUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useWatchProjectData } from '@/views/WatchProjectView/useWatchProjectData'
import { FranchiseMediaHorizontalScroller } from './FranchiseMediaHorizontalScroller'

export interface FranchiseProjectProps {
  className?: string
  project: Project
  isGuildMember: boolean
  defaultProject?: Project
  pageData?: FranchisePageData | null
  hasWatchableReasons?: boolean
  containerPosition?: number
}

export const FranchiseProject: React.FC<FranchiseProjectProps> = ({
  className,
  project,
  isGuildMember,
  defaultProject,
  pageData,
  hasWatchableReasons = false,
  containerPosition = 0,
}) => {
  const { preferredSeason, currentEpisode } = useWatchProjectData({
    data: project,
    skip: !project?.seasons?.length,
    isGuildMember,
  })
  const contentfulProject = pageData?.projectsCollection?.items?.filter(
    (contentfulProject) => contentfulProject?.slug === project?.slug,
  )[0]
  const [selectedSeason, setSelectedSeason] = useState<Season | undefined>(preferredSeason)
  const [movieSpecialMedias, setMovieSpecialMedias] = useState<Media[]>([])
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const track = useSafeTrack()
  const { trackInteraction } = useContentInteraction()

  // Poster tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)
  const [posterRef, isPosterVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9, // Track when at least 90% of the poster is visible
  })

  const hasTickets = isSellingTickets(project?.primaryFlowPhases ?? [])
  const isProjectInEarlyAccess = Boolean(
    isInGuildAccess(project?.primaryFlowPhases ?? []) || getProjectInEarlyAccess(project),
  )
  const isProjectSellingTickets = isSellingTickets(project?.primaryFlowPhases ?? [])
  const movieLandscapeAngelArt3 = defaultProject?.title?.landscapeAngelImage3?.cloudinaryPath
  const moviePortraitAngelArt3 = defaultProject?.title?.portraitAngelImage3?.cloudinaryPath
  const landscapeProjectPoster =
    project?.title?.landscapeAngelImage?.cloudinaryPath ??
    project?.title?.landscapeTitleImage?.cloudinaryPath ??
    movieLandscapeAngelArt3
  const portraitProjectPoster =
    project?.title?.portraitAngelImage?.cloudinaryPath ??
    project?.title?.portraitTitleImage?.cloudinaryPath ??
    moviePortraitAngelArt3
  const medias = processEpisodesToMediasWithFiltering(
    selectedSeason?.episodes,
    {
      projectName: project?.name,
      hasEarlyAccess: false,
      projectType: project?.projectType,
      trailerText: t('trailer', 'Trailer'),
      locale,
      selectedSeasonId: selectedSeason?.id || '',
      isGuildMember,
    },
    false,
    project,
  )

  // Load movie/special watchables if there are no episodes to show (non-series titles)
  useEffect(() => {
    let cancelled = false
    async function loadWatchables() {
      const noSeasons = !project?.title?.seasons || project.title?.seasons?.edges?.length === 0
      const isNonSeries = project?.projectType && project.projectType !== 'series'

      if (isNonSeries && noSeasons) {
        const client = getWebClient({ locale })
        const { watchables } = await getMovieAndSpecialsWatchables(project.slug, client)
        if (cancelled) return
        const mapped = processWatchablesToMediasWithFiltering(
          watchables,
          {
            projectName: project?.name,
            hasEarlyAccess: false,
            projectType: project?.projectType,
            trailerText: t('trailer', 'Trailer'),
            locale,
          },
          false,
          isGuildMember,
        )
        startTransition(() => {
          setMovieSpecialMedias(mapped)
        })
      } else {
        startTransition(() => {
          setMovieSpecialMedias([])
        })
      }
    }
    loadWatchables()
    return () => {
      cancelled = true
    }
  }, [
    project.slug,
    project.projectType,
    project.seasons.length,
    project.title?.seasons,
    t,
    locale,
    project?.name,
    isGuildMember,
  ])
  const availabilitySubtitle = buildSubtitle(project, t, locale, isGuildMember)
  const contentRating = project?.title?.ratings?.find((rating: string) => !!rating)
  const onlyMovieAvailable = Boolean(
    medias && medias.length === 1 && isStreaming(project?.primaryFlowPhases ?? []) && project?.projectType === 'movie',
  )
  const projectDescription =
    project?.title && 'description' in project.title
      ? (project?.title?.description as string)
      : project?.theaterDescription
  const canStream = canStreamEpisode(isGuildMember, currentEpisode)
  const showHeroProps = useMemo(() => {
    if (isARecap(currentEpisode)) {
      const ctaText = t('watchRecap', 'Watch Recap')
      return getTrailerCta({
        ctaText,
        currentEpisode,
        locale,
        slug: project?.slug,
        theaterDescription: projectDescription,
      })
    } else if (isASneakPeek(currentEpisode)) {
      const ctaText = t('watchSneakPeek', 'Watch Sneak Peek')
      return getTrailerCta({
        ctaText,
        currentEpisode,
        locale,
        slug: project?.slug,
        theaterDescription: projectDescription,
      })
    } else if (currentEpisode?.isTrailer) {
      const episodeName = currentEpisode?.name ?? 'Trailer'
      const ctaText = episodeName.toLowerCase().includes('trailer')
        ? t('watchTrailer', 'Watch Trailer')
        : t('watchEpisodeName', 'Watch {{ episodeName }}', {
            episodeName,
          })
      return getTrailerCta({
        ctaText,
        currentEpisode,
        locale,
        slug: project?.slug,
        theaterDescription: projectDescription,
      })
    } else if (currentEpisode && canStream) {
      const ctaText = t('startWatching', 'Start Watching')
      return getDefaultCta({
        ctaText,
        currentEpisode,
        locale,
        slug: project?.slug,
        theaterDescription: projectDescription,
      })
    } else {
      const ctaText = t('learnMore', 'Learn More')
      return {
        ctaText,
        ctaPath: `${paths.watch.index}/${project?.slug}`,
        theaterDescription: projectDescription,
      }
    }
  }, [currentEpisode, canStream, t, locale, project?.slug, projectDescription])

  const handleClick = (variant: string) => {
    switch (variant) {
      case 'getShowtimes':
        track('Clicked Franchise Page CTA Get Showtimes', {
          slug: project?.slug,
          project,
        })
        break
      case 'watchSeries':
        track('Clicked Franchise Page CTA Watch Series', {
          slug: project?.slug,
          project,
        })
        break
    }
  }

  // Poster interaction handlers
  const handlePosterMouseEnter = useCallback(() => {
    const projectId = project?.title?.id || project?.id
    if (!projectId) return

    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [project?.title?.id, project?.id])

  const handlePosterMouseLeave = useCallback(
    (mediaFormat: 'portrait_display' | 'landscape_display', posterPath: string) => {
      const projectId = project?.title?.id || project?.id
      const projectTypeName = project?.title?.__typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial' | null

      if (!projectId || !hoverStartTimeRef.current || !projectTypeName) return

      const dwellTime = Date.now() - hoverStartTimeRef.current

      // Only track hover if the user hovered for at least the threshold time
      if (dwellTime >= HOVER_THRESHOLD_MS && posterPath) {
        const interaction = new FranchiseLandingItemInteraction({
          id: projectId,
          __typename: projectTypeName,
          attribution: {
            containerPosition,
            image: {
              cloudinaryPath: posterPath,
            },
          },
        })
          .withSurface('generic_list')
          .withPlacement('title_option', containerPosition, 0, 'single_feature')
          .withImageArtwork(posterPath, mediaFormat)
          .withHover(dwellTime)

        trackInteraction(interaction)
      }

      // Reset state
      hoverStartTimeRef.current = null
    },
    [project?.title?.id, project?.id, project?.title?.__typename, containerPosition, trackInteraction],
  )

  const handlePosterClick = useCallback(
    (mediaFormat: 'portrait_display' | 'landscape_display', posterPath: string) => {
      const projectId = project?.title?.id || project?.id
      const projectTypeName = project?.title?.__typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial' | null

      if (!projectId || !posterPath || !projectTypeName) return

      const interaction = new FranchiseLandingItemInteraction({
        id: projectId,
        __typename: projectTypeName,
        attribution: {
          containerPosition,
          image: {
            cloudinaryPath: posterPath,
          },
        },
      })
        .withSurface('generic_list')
        .withPlacement('title_option', containerPosition, 0, 'single_feature')
        .withImageArtwork(posterPath, mediaFormat)
        .withPress()

      trackInteraction(interaction)
    },
    [project?.title?.id, project?.id, project?.title?.__typename, containerPosition, trackInteraction],
  )

  // Track impression when the poster becomes visible for at least 1 second
  useEffect(() => {
    const projectId = project?.title?.id || project?.id
    const projectTypeName = project?.title?.__typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial' | null

    if (!projectId || hasTrackedImpression.current || !projectTypeName) return

    if (isPosterVisible) {
      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true

        // Determine which poster is actually visible based on responsive breakpoints
        // Landscape: mobile (< md) and desktop (xl+)
        // Portrait: tablet (md to xl)
        const isTablet = typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1280
        const visiblePosterPath = isTablet ? portraitProjectPoster : landscapeProjectPoster
        const mediaFormat: 'portrait_display' | 'landscape_display' = isTablet
          ? 'portrait_display'
          : 'landscape_display'

        if (visiblePosterPath) {
          const interaction = new FranchiseLandingItemInteraction({
            id: projectId,
            __typename: projectTypeName,
            attribution: {
              containerPosition,
              image: {
                cloudinaryPath: visiblePosterPath,
              },
            },
          })
            .withSurface('generic_list')
            .withPlacement('title_option', containerPosition, 0, 'single_feature')
            .withImageArtwork(visiblePosterPath, mediaFormat)
            .withImpression()

          trackInteraction(interaction)
        }
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if poster becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [
    isPosterVisible,
    project?.title?.id,
    project?.id,
    project?.title?.__typename,
    landscapeProjectPoster,
    portraitProjectPoster,
    trackInteraction,
    containerPosition,
  ])

  useEffect(() => {
    if (preferredSeason) {
      setSelectedSeason(preferredSeason)
    }
  }, [preferredSeason])

  const genres =
    project?.title && 'genres' in project.title && Array.isArray(project.title.genres) ? project.title.genres : []

  return (
    <div
      className={classNames(
        'flex flex-col lg:py-28 2xl:py-40 py-8 md:py-16 text-white max-w-[1680px] 3lg:mx-auto',
        className,
      )}
    >
      <div
        className={classNames(
          'relative flex flex-col gap-8 lg:gap-4 xl:gap-[88px] md:flex-row md:justify-between px-4 md:px-8 lg:px-16',
          className,
        )}
      >
        <div className="order-2 flex flex-col gap-4 md:order-1">
          <When condition={contentfulProject?.franchiseWatchOrderText}>
            <EyebrowSM weight="medium" className="xl:photon-eyebrow-md">
              {contentfulProject?.franchiseWatchOrderText}
            </EyebrowSM>
          </When>
          <TitleMD weight="bold" className="md:photon-heading-xs lg:photon-heading-sm">
            {project?.name}
          </TitleMD>
          <ParagraphSM
            color={'core-gray-400'}
            className="lg:photon-paragraph-lg lg:max-w-[519px] xl:max-w-[615px] 2xl:max-w-[712px]"
          >
            {projectDescription}
          </ParagraphSM>
          <div className="flex flex-row flex-wrap justify-start gap-2 text-core-gray-400">
            <CaptionMD className="whitespace-nowrap">
              <Switch>
                <Case condition={project?.projectType === projectTypes.series}>
                  <Translate i18nKey="tvSeries" t={t}>
                    TV Series
                  </Translate>
                </Case>
                <Case condition={project?.projectType === projectTypes.movie}>
                  <Translate i18nKey="movie" t={t}>
                    Movie
                  </Translate>
                </Case>
                <Case condition={project?.projectType === projectTypes.podcast}>
                  <Translate i18nKey="podcast" t={t}>
                    Podcast
                  </Translate>
                </Case>
                <Case condition={project?.projectType === projectTypes.special}>
                  <Translate i18nKey="special" t={t}>
                    Special
                  </Translate>
                </Case>
              </Switch>
            </CaptionMD>
            {genres && genres.length > 0 && <CaptionMD>•</CaptionMD>}
            {genres.map((genre: string, index: number) => {
              return (
                <div className="flex gap-2" key={genre}>
                  <CaptionMD>{genre}</CaptionMD>
                  <When condition={genres?.length && index < genres.length - 1}>
                    <CaptionMD>•</CaptionMD>
                  </When>
                </div>
              )
            })}
          </div>
          <div className="flex flex-row items-center gap-2">
            <When condition={project?.highestScore}>
              <GuildScore guildScore={project?.highestScore} />
            </When>
            <ContentRatingBadge contentRating={contentRating} />
            <HighDefinitionBadge slug={project?.slug} />
            <ClosedCaptionsBadge />
          </div>
          <When condition={availabilitySubtitle}>
            <CaptionMD color="info-200" weight="medium" className="flex flex-row gap-2">
              <PlaySquareIcon color="info-200" size={18} />
              {availabilitySubtitle}
            </CaptionMD>
          </When>
          <When condition={isProjectInEarlyAccess || isProjectSellingTickets}>
            <CaptionMD color="core-warning-200" weight="medium" className="flex flex-row gap-2">
              <When condition={isGuildMember}>
                <CircleCheckOutlineIcon color="core-warning-200" />
                {t('includedWithGuildMembership', 'Included with your Guild Membership')}
              </When>
              <When condition={!isGuildMember}>
                <CircleCheckOutlineIcon color="core-warning-200" />
                <When condition={isProjectInEarlyAccess}>
                  {t('streamingGuildMembers', 'Streaming For Guild Members')}
                </When>
                <When condition={isProjectSellingTickets}>
                  {t('ticketsForGuildMembers', 'Tickets For Guild Members')}
                </When>
              </When>
            </CaptionMD>
          </When>
          <div className="flex flex-col md:flex-row">
            <LinkButton
              variant="white"
              href={showHeroProps?.ctaPath ?? ''}
              onClick={() => handleClick('watchSeries')}
              className="flex min-h-10 w-fit items-center justify-center gap-1 lg:px-4 xl:px-6"
            >
              {currentEpisode && canStream && <PlayCircleOutlineIcon color="black" />}
              <LabelSM className="xl:photon-label-md">{showHeroProps?.ctaText}</LabelSM>
            </LinkButton>
            {hasTickets && (
              <LinkButton
                variant="white"
                href={`${paths.tickets.index}/${project?.slug}`}
                onClick={() => handleClick('getShowtimes')}
                className="mt-2 flex min-h-10 w-fit items-center justify-center gap-1 md:ml-4 md:mt-0 lg:ml-8 lg:px-4 xl:px-6"
              >
                <TicketIcon color="black" />
                <LabelSM className="xl:photon-label-md">{t('getShowtimes', 'Get Showtimes')}</LabelSM>
              </LinkButton>
            )}
          </div>
        </div>
        <div
          ref={posterRef}
          className="order-1 md:order-2 md:max-w-[288px] lg:max-w-[400px] xl:max-w-[510px] 2xl:max-w-[720px] 3xl:max-w-[850px]"
        >
          <Link
            href={`${paths.watch.index}/${project?.slug}`}
            className="block cursor-pointer md:hidden xl:block"
            onMouseEnter={handlePosterMouseEnter}
            onMouseLeave={() => handlePosterMouseLeave('landscape_display', landscapeProjectPoster as string)}
            onClick={() => handlePosterClick('landscape_display', landscapeProjectPoster as string)}
          >
            <Image
              className="w-full rounded-2xl object-contain"
              src={landscapeProjectPoster as string}
              alt={`${project?.name} poster`}
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: 'auto' }}
            />
          </Link>
          <Link
            href={`${paths.watch.index}/${project?.slug}`}
            className="hidden cursor-pointer md:block xl:hidden"
            onMouseEnter={handlePosterMouseEnter}
            onMouseLeave={() => handlePosterMouseLeave('portrait_display', portraitProjectPoster as string)}
            onClick={() => handlePosterClick('portrait_display', portraitProjectPoster as string)}
          >
            <Image
              className="w-full rounded-2xl object-contain"
              src={portraitProjectPoster as string}
              alt={`${project?.name} poster`}
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: 'auto' }}
            />
          </Link>
        </div>
      </div>
      {(project?.projectType !== 'series' ? movieSpecialMedias : medias)?.length > 0 && !onlyMovieAvailable && (
        <div className="mt-8 flex w-full flex-col">
          <TitleSM weight="semibold" className="lg:photon-title-lg z-20 px-4 pb-6 md:px-8 lg:px-16">
            <When condition={project?.projectType?.toLowerCase() !== 'movie'}>{t('episodes', 'Episodes')}</When>
            <When condition={project?.projectType?.toLowerCase() === 'movie'}>{t('extras', 'Extras')}</When>
          </TitleSM>
          <FranchiseMediaHorizontalScroller
            medias={project?.projectType !== 'series' ? movieSpecialMedias : medias}
            padding
            hasWatchableReasons={hasWatchableReasons}
            containerPosition={containerPosition}
            contentTypeName={project?.projectType === 'series' ? 'ContentEpisode' : 'ContentLivestream'}
          />
        </div>
      )}
    </div>
  )
}
